/**
 * ImpersonateButton — « Naviguer en tant que ce client » (Batch #14.338). Réservé à l'administrateur principal
 * (bouton grisé sinon). Modal : motif facultatif + mode (lecture seule / test) → jeton 15 min → ouverture du portail
 * dans un nouvel onglet (bouton synchrone, donc jamais bloqué par le navigateur).
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Eye, ExternalLink, FlaskConical, Loader2, ShieldAlert, X } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { putHandoff } from "@/lib/portalSession";

let canCache = null; // {allowed, minutes} — même réponse pour tous les boutons de la page

export function useCanImpersonate() {
  const [can, setCan] = useState(canCache);
  useEffect(() => {
    if (canCache) return;
    api.get("/admin/impersonation/can").then((r) => { canCache = r.data; setCan(r.data); }).catch(() => setCan({ allowed: false }));
  }, []);
  return can;
}

export default function ImpersonateButton({ client, compact = false, className = "" }) {
  const can = useCanImpersonate();
  const [open, setOpen] = useState(false);
  const enabled = !!client?.portal_enabled && !!can?.allowed;
  const why = !client?.portal_enabled ? "Ce client n'a pas d'accès portail actif" : !can?.allowed ? "Réservé à l'administrateur principal" : `Ouvrir le portail de ce client (lecture seule, ${can?.minutes || 15} min)`;
  return (
    <>
      <button
        type="button"
        onClick={() => enabled && setOpen(true)}
        disabled={!enabled}
        title={why}
        className={className || `px-2.5 py-1.5 rounded-md text-xs bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1`}
        data-testid={`impersonate-btn-${client?.id}`}
      >
        <Eye className="w-3 h-3" /> {compact ? "En tant que" : "Naviguer en tant que"}
      </button>
      {open && <ImpersonateModal client={client} minutes={can?.minutes || 15} onClose={() => setOpen(false)} />}
    </>
  );
}

function ImpersonateModal({ client, minutes, onClose }) {
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("readonly");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(null); // {key, expires_at}
  const name = `${client.first_name || ""} ${client.last_name || ""}`.trim() || client.nom || client.email;

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/impersonate/${client.id}`, { reason: reason.trim() || null, mode });
      const key = putHandoff(r.data);
      setReady({ key, expires_at: r.data.expires_at });
      toast.success(`Session « en tant que » ouverte pour ${minutes} min — journalisée`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir la session");
    } finally { setBusy(false); }
  };
  const openTab = () => {
    const w = window.open(`/portal/as/${ready.key}`, "_blank");
    if (!w) toast.error("Fenêtre bloquée — autorisez les fenêtres surgissantes");
    else onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4 text-left" onClick={onClose} data-testid="impersonate-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-red-50 rounded-t-2xl">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-700" /> Naviguer en tant que {name}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-red-100" data-testid="impersonate-close"><X className="w-4 h-4" /></button>
        </div>
        {!ready ? (
          <div className="p-5 space-y-4">
            <p className="text-sm text-slate-600">Le portail s'ouvre dans un nouvel onglet avec un jeton temporaire de <strong>{minutes} minutes</strong>, sans jamais utiliser le mot de passe du client. L'action est inscrite au journal d'audit.</p>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Motif (facultatif)</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="ex. soutien téléphonique, vérification d'un document"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="impersonate-reason" />
            </div>
            <div className="space-y-2">
              <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${mode === "readonly" ? "border-red-400 bg-red-50/50" : "border-slate-200"}`}>
                <input type="radio" name="imp-mode" checked={mode === "readonly"} onChange={() => setMode("readonly")} className="mt-0.5 accent-red-700" data-testid="impersonate-mode-readonly" />
                <span className="text-sm"><span className="font-semibold inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Lecture seule</span><br /><span className="text-xs text-slate-500">Vous voyez tout, aucune action n'est enregistrée (recommandé).</span></span>
              </label>
              <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${mode === "test" ? "border-amber-400 bg-amber-50/50" : "border-slate-200"}`}>
                <input type="radio" name="imp-mode" checked={mode === "test"} onChange={() => setMode("test")} className="mt-0.5 accent-amber-600" data-testid="impersonate-mode-test" />
                <span className="text-sm"><span className="font-semibold inline-flex items-center gap-1"><FlaskConical className="w-3.5 h-3.5" /> Mode test</span><br /><span className="text-xs text-slate-500">Actions permises pour tester un parcours, SAUF paiements, mot de passe, signatures, soumissions et suppressions. Journalisé.</span></span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm rounded-lg border border-slate-300" data-testid="impersonate-cancel">Annuler</button>
              <button onClick={start} disabled={busy} className="px-4 py-2 text-sm rounded-lg bg-red-700 text-white hover:bg-red-800 disabled:opacity-50 inline-flex items-center gap-2" data-testid="impersonate-start">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />} Ouvrir la session
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4 text-center">
            <p className="text-sm text-slate-700">Session prête (expire à {new Date(ready.expires_at).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}). Un bandeau rouge vous rappellera que vous êtes dans le compte du client.</p>
            <button onClick={openTab} className="w-full px-4 py-3 text-sm font-semibold rounded-xl bg-red-700 text-white hover:bg-red-800 inline-flex items-center justify-center gap-2" data-testid="impersonate-open-tab">
              <ExternalLink className="w-4 h-4" /> Ouvrir le portail dans un nouvel onglet
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
